import { CHARGE_MODES, BILLING_CYCLES, FIELD, LABEL, flagsFromChargeMode } from "./planFormShared";
import { BTN_PRIMARY, BTN_SECONDARY, MODAL_BACKDROP } from "../../ui/pageChrome";
import { CloseIcon } from "../../Icons";
import DateInput from "../../components/datePicker/DateInput";

function NumberField({ label, value, onChange, disabled }) {
  return (
    <label className="block">
      <span className={LABEL}>{label}</span>
      <input
        type="number"
        min="0"
        step="0.01"
        disabled={disabled}
        className={FIELD}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

export function ChargeModeFields({ form, setForm }) {
  const flags = flagsFromChargeMode(form.billingChargeMode);
  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <>
      <div>
        <p className={LABEL}>Who this plan bills</p>
        <div className="flex flex-wrap gap-2">
          {CHARGE_MODES.map((mode) => (
            <button
              key={mode.id}
              type="button"
              onClick={() => setForm((prev) => ({ ...prev, billingChargeMode: mode.id }))}
              className={`h-8 px-3 text-xs font-medium rounded-md border ${
                form.billingChargeMode === mode.id
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {mode.label}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Employee and patient basics are billed as separate invoice rows. Extra users are billed on the next row in that section.
        </p>
      </div>

      {flags.chargeEmployees ? (
        <section className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 space-y-3">
          <h3 className="text-xs font-bold uppercase tracking-wide text-indigo-800">
            Employee plan
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <NumberField
              label="Employee basic amount"
              value={form.baseAmount}
              onChange={set("baseAmount")}
            />
            <NumberField
              label="Included employees"
              value={form.includedUsers}
              onChange={set("includedUsers")}
            />
            <NumberField
              label="Price per extra employee"
              value={form.pricePerExtraUser}
              onChange={set("pricePerExtraUser")}
            />
            <NumberField
              label="Max employees (optional)"
              value={form.maxUsers}
              onChange={set("maxUsers")}
            />
          </div>
        </section>
      ) : null}

      {flags.chargePatients ? (
        <section className="rounded-lg border border-sky-100 bg-sky-50/50 p-3 space-y-3">
          <h3 className="text-xs font-bold uppercase tracking-wide text-sky-800">
            Patient plan
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <NumberField
              label="Patient basic amount"
              value={form.patientBaseAmount}
              onChange={set("patientBaseAmount")}
            />
            <NumberField
              label="Included patients"
              value={form.includedPatients}
              onChange={set("includedPatients")}
            />
            <NumberField
              label="Price per extra patient"
              value={form.pricePerExtraPatient}
              onChange={set("pricePerExtraPatient")}
            />
            <NumberField
              label="Max patients (optional)"
              value={form.maxPatients}
              onChange={set("maxPatients")}
            />
          </div>
        </section>
      ) : null}
    </>
  );
}

export function PlanEditorModal({ title, form, setForm, saving, onClose, onSave, isEdit }) {
  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <div className={MODAL_BACKDROP}>
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          <button type="button" onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700">
            <CloseIcon size={16} />
          </button>
        </div>
        <form
          className="p-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSave();
          }}
        >
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block sm:col-span-2">
              <span className={LABEL}>Plan name</span>
              <input className={FIELD} value={form.name} onChange={(e) => set("name")(e.target.value)} required />
            </label>
            <label className="block sm:col-span-2">
              <span className={LABEL}>Description</span>
              <textarea
                className={`${FIELD} h-20 py-2`}
                value={form.description}
                onChange={(e) => set("description")(e.target.value)}
              />
            </label>
            <label className="block">
              <span className={LABEL}>Billing cycle</span>
              <select
                className={FIELD}
                value={form.billingCycle}
                onChange={(e) => {
                  const cycle = BILLING_CYCLES.find((c) => c.id === e.target.value);
                  setForm((prev) => ({
                    ...prev,
                    billingCycle: e.target.value,
                    billingCycleMonths: String(cycle?.months ?? prev.billingCycleMonths),
                  }));
                }}
              >
                {BILLING_CYCLES.map((cycle) => (
                  <option key={cycle.id} value={cycle.id}>
                    {cycle.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className={LABEL}>Payment model</span>
              <select className={FIELD} value={form.paymentModel} onChange={(e) => set("paymentModel")(e.target.value)}>
                <option value="POSTPAID">Postpaid</option>
                <option value="PREPAID">Prepaid</option>
              </select>
            </label>
            <NumberField label="Grace period (days)" value={form.gracePeriodDays} onChange={set("gracePeriodDays")} />
          </div>

          <ChargeModeFields form={form} setForm={setForm} />

          <div className="flex flex-wrap items-center gap-4">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.status === "Active"}
                onChange={(e) => set("status")(e.target.checked ? "Active" : "Inactive")}
              />
              Active
            </label>
            {isEdit ? (
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={Boolean(form.syncActiveAssignments)}
                  onChange={(e) => set("syncActiveAssignments")(e.target.checked)}
                />
                Also update active assigned plans
              </label>
            ) : null}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className={BTN_SECONDARY} onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className={BTN_PRIMARY} disabled={saving || !form.name.trim()}>
              {saving ? "Saving..." : "Save plan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function AssignedPlanEditorModal({
  title,
  form,
  setForm,
  saving,
  onClose,
  onSave,
  plans,
  tenants,
  isEdit,
}) {
  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <div className={MODAL_BACKDROP}>
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          <button type="button" onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700">
            <CloseIcon size={16} />
          </button>
        </div>
        <form
          className="p-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSave();
          }}
        >
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className={LABEL}>Tenant</span>
              <select
                className={FIELD}
                value={form.tenantId}
                disabled={isEdit}
                onChange={(e) => set("tenantId")(e.target.value)}
                required
              >
                <option value="">Select tenant</option>
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.companyName || tenant.name} (#{tenant.id})
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className={LABEL}>Catalog plan</span>
              <select
                className={FIELD}
                value={form.planId}
                onChange={(e) => set("planId")(e.target.value)}
                required
              >
                <option value="">Select plan</option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className={LABEL}>Start date</span>
              <DateInput className={FIELD} value={form.startDate} onChange={(e) => set("startDate")(e.target.value)} required />
            </label>
            <label className="block">
              <span className={LABEL}>End date (next bill)</span>
              <DateInput className={FIELD} value={form.endDate} onChange={(e) => set("endDate")(e.target.value)} />
            </label>
            {isEdit ? (
              <label className="block">
                <span className={LABEL}>Status</span>
                <select className={FIELD} value={form.status} onChange={(e) => set("status")(e.target.value)}>
                  <option value="ACTIVE">Active</option>
                  <option value="SUSPENDED">Suspended</option>
                  <option value="EXPIRED">Expired</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </label>
            ) : null}
          </div>

          <ChargeModeFields form={form} setForm={setForm} />

          <div className="grid sm:grid-cols-2 gap-3">
            <NumberField label="Grace period (days)" value={form.gracePeriodDays} onChange={set("gracePeriodDays")} />
          </div>

          {isEdit ? (
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={Boolean(form.refreshSnapshotFromPlan)}
                onChange={(e) => set("refreshSnapshotFromPlan")(e.target.checked)}
              />
              Reset snapshot from catalog plan first
            </label>
          ) : null}

          <label className="block">
            <span className={LABEL}>Notes</span>
            <textarea className={`${FIELD} h-16 py-2`} value={form.notes} onChange={(e) => set("notes")(e.target.value)} />
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className={BTN_SECONDARY} onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className={BTN_PRIMARY} disabled={saving || !form.tenantId || !form.planId}>
              {saving ? "Saving..." : isEdit ? "Update assigned plan" : "Assign plan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
